/**
 * Диалоги правки из окна карточки площадки: подтверждение и ход отправки.
 *
 * Подтверждение — прежний `CardEditConfirmDialog` с текстом про журнал правок.
 * После подтверждения открывается диалог прогресса фоновой задачи: шаги
 * («Разворот правки в набор», «Отправка на площадку»), затем честный итог —
 * отправлено, отклонено площадкой с причиной или сохранено без отправки.
 */

import React from 'react'
import { AlertTriangle, CheckCircle2, Circle, Info, Loader2, XCircle } from 'lucide-react'
import { Button } from '../../../../components/ui/Button'
import { Modal } from '../../../../components/ui/Modal'
import { cn } from '../../../../lib/cn'
import { CardEditConfirmDialog } from '../CardEditConfirmDialog'
import { PLATFORM_NAME, type McardPlatform } from './model'
import type { McardEditApi } from './useMpCardEdit'
import { WINDOW_EDIT_OUTCOME_LABELS, type CardWindowState } from './windowEditApi'

const SETTINGS_LINK_HREF = '/settings/marketplace-link'

/** Почему отправка на площадку сейчас выключена; null — включена. */
export const sendDisabledReason = (
  platform: McardPlatform,
  state: CardWindowState | null,
  changedKeys: { text: boolean },
): string | null => {
  if (!state) return null
  if (!state.send.enabled) return 'Отправка правок на площадки выключена'
  if (platform === 'ym' && !state.send.ym_write_enabled) return 'Запись в кабинет Яндекс Маркета выключена'
  if (platform === 'wb' && changedKeys.text && !state.send.wb_text_enabled) {
    return 'Запись текста, характеристик, бренда и габаритов в кабинет Wildberries выключена'
  }
  return null
}

interface ConfirmProps {
  edit: McardEditApi
  platform: McardPlatform
  cabinet: string
  state: CardWindowState | null
}

const stepIcon = (status: string) => {
  if (status === 'completed') return <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
  if (status === 'processing') return <Loader2 className="h-4 w-4 animate-spin text-indigo-600 dark:text-indigo-400" />
  if (status === 'failed') return <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
  return <Circle className="h-4 w-4 text-gray-300 dark:text-zinc-600" />
}

const toneBox: Record<'ok' | 'warn' | 'bad' | 'info', string> = {
  ok: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300',
  warn: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200',
  bad: 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300',
  info: 'border-indigo-200 bg-indigo-50 text-indigo-800 dark:border-indigo-900 dark:bg-indigo-950/30 dark:text-indigo-200',
}

export const WindowEditDialogs: React.FC<ConfirmProps> = ({ edit, platform, cabinet, state }) => {
  const { phase, outcome, error, job } = edit
  // Бренд и габариты на ВБ уходят тем же запросом cards/update, что и текст.
  const hasText =
    edit.changes.title !== undefined ||
    edit.changes.description !== undefined ||
    Boolean(edit.changes.characteristics) ||
    edit.changes.brand !== undefined ||
    edit.changes.dimensions !== undefined
  const offReason = sendDisabledReason(platform, state, { text: hasText })
  const photosChanged = edit.changes.media_urls !== undefined
  const warnings: string[] = []
  if (photosChanged && platform === 'wb') {
    warnings.push('Wildberries заменит набор фотографий целиком. Удалённое изображение восстановить из карточки нельзя.')
  } else if (photosChanged) {
    warnings.push('Набор фотографий будет заменён целиком в том порядке, который вы задали.')
  }

  const notice = offReason ? (
    <>
      Правка сохранится в ПИМ и журнале; на площадку уйдёт, когда отправку включат. {offReason}.
    </>
  ) : (
    <>Правка сохранится в ПИМ и уйдёт на площадку через журнал правок.</>
  )

  const running = phase === 'running'
  const progressOpen = phase === 'running' || phase === 'done' || phase === 'failed'
  const tone = outcome ? WINDOW_EDIT_OUTCOME_LABELS[outcome.status].tone : phase === 'failed' ? 'bad' : 'info'

  return (
    <>
      <CardEditConfirmDialog
        isOpen={phase === 'confirm' || phase === 'saving'}
        platform={PLATFORM_NAME[platform]}
        cabinet={cabinet}
        title="Сохранить правку и отправить на площадку?"
        changedFields={edit.dirtyLabels.length ? edit.dirtyLabels : ['Нет изменений']}
        violations={edit.violations}
        warning={warnings.length > 0 ? warnings.join(' ') : undefined}
        dryRun={Boolean(offReason)}
        notice={notice}
        confirmLabel="Сохранить и отправить"
        saving={phase === 'saving'}
        onClose={() => {
          if (phase !== 'saving') edit.closeConfirm()
        }}
        onConfirm={() => void edit.confirm()}
      />

      <Modal
        isOpen={progressOpen}
        onClose={() => {
          if (!running) edit.finish()
        }}
        title="Отправка правки"
        subtitle={`${PLATFORM_NAME[platform]} · кабинет «${cabinet}»`}
        layer="nested"
        closeOnOverlayClick={!running}
        closeOnEsc={!running}
        showCloseButton={!running}
        footer={
          <Button disabled={running} onClick={edit.finish}>
            Закрыть
          </Button>
        }
      >
        <div className="space-y-3 text-sm" aria-live="polite">
          {job?.steps && job.steps.length > 0 && (
            <ol className="space-y-1.5">
              {job.steps.map((step) => (
                <li key={step.name} className="flex items-center gap-2 text-xs text-gray-700 dark:text-zinc-300">
                  {stepIcon(step.status)}
                  <span className={cn(step.status === 'pending' && 'text-gray-400 dark:text-zinc-500')}>{step.name}</span>
                </li>
              ))}
            </ol>
          )}
          {running && (!job?.steps || job.steps.length === 0) && (
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-zinc-300">
              <Loader2 className="h-4 w-4 animate-spin" />
              Задача запущена, ждём ответа
            </div>
          )}

          {outcome && (
            <div className={cn('flex gap-2 rounded-md border px-3 py-2.5 text-xs', toneBox[tone])}>
              {tone === 'ok' ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              ) : tone === 'bad' ? (
                <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
              ) : tone === 'warn' ? (
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              ) : (
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
              )}
              <div className="min-w-0">
                <div className="font-medium">{WINDOW_EDIT_OUTCOME_LABELS[outcome.status].label}</div>
                {outcome.message && outcome.message !== WINDOW_EDIT_OUTCOME_LABELS[outcome.status].label && (
                  <div className="mt-0.5">{outcome.message}</div>
                )}
                {outcome.reason && <div className="mt-0.5 break-words">Причина: {outcome.reason}</div>}
              </div>
            </div>
          )}

          {error && (
            <div className={cn('flex gap-2 rounded-md border px-3 py-2.5 text-xs', toneBox.bad)}>
              <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="min-w-0">
                <div className="font-medium">Правка не выполнена</div>
                <div className="mt-0.5 break-words">{error.message}</div>
                {error.code === 'CARD_NOT_LINKED' && (
                  <a href={SETTINGS_LINK_HREF} className="mt-1 inline-block underline">
                    Открыть «Связывание с PIM»
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  )
}

export default WindowEditDialogs
